import { Field, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import callApi from '../../utils/callApi';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../utils/tracking';
import useDeepCompare from '../../utils/useDeepCompare';
import useScript, { mountScript } from '../../utils/useScript';
import CheckboxField from '../formik/CheckboxField';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Klarna: any;
  }
}

// https://docs.klarna.com/payments/web-payments/additional-resources/klarna-payments-sdk-reference/
const KlarnaPayment = () => {
  const scriptLoadingPromise = useScript('https://x.klarnacdn.net/kp/lib/v1/api.js');
  const { checkoutState } = useCheckoutState();
  const { formatMessage } = useIntl();
  const { values } = useFormikContext<{
    paymentMethod: string;
    storeKlarnaPaymentMethod: boolean;
  }>();
  const selectedPaymentMethod = checkoutState.orderState.payment?.selected?.[0];
  const initializationData = useDeepCompare(selectedPaymentMethod?.configuration?.initializationData);
  const basketItems = useDeepCompare(
    checkoutState.orderState.basket?.packages
      ?.flatMap(pkg => pkg.items)
      .map(({ variantId, quantity, total }) => ({ variantId, quantity, total }))
  );

  useEffect(() => {
    if (
      !initializationData ||
      !basketItems.length ||
      selectedPaymentMethod?.paymentOptionKey !== values.paymentMethod
    ) {
      return;
    }

    (async () => {
      await scriptLoadingPromise.current;
      try {
        // Remove this log after SPAY klarna integration is done - Piotr Daszko
        if (!config.environment?.includes('live')) {
          console.log('Klarna init payload: ', { client_token: initializationData.clientToken });
        }
        window.Klarna.Payments.init({
          client_token: initializationData.clientToken
        });

        // Remove this log after SPAY klarna integration is done - Piotr Daszko
        if (!config.environment?.includes('live')) {
          console.log('Klarna load payload: ', {
            container: '#klarna-payment-container',
            payment_method_category: initializationData.paymentCategory
          });
        }
        window.Klarna.Payments.load({
          container: '#klarna-payment-container',
          payment_method_category: initializationData.paymentCategory
        });
      } catch (e) {
        console.error(e);
        pushErrorTrackingEventFromErrorObject(e, 'klarna');
      }
    })();
  }, [initializationData, basketItems]);

  useEffect(
    () => () => {
      if (IS_WEB_COMPONENT && !import.meta.env.DEV) {
        const klarnaPaymentContainer = document.getElementById('klarna-payment-container');
        if (klarnaPaymentContainer) {
          klarnaPaymentContainer.innerHTML = '';
        }
      }
    },
    [basketItems]
  );

  if (!selectedPaymentMethod) {
    return null;
  }

  return (
    <div className="option__form">
      {IS_WEB_COMPONENT && !import.meta.env.DEV ?
        <slot name="klarna-payment-container" />
      : <div id="klarna-payment-container" />}
      {values.paymentMethod === selectedPaymentMethod!.paymentOptionKey &&
        selectedPaymentMethod?.configuration?.isRecurring && (
          <Field
            name="storeKlarnaPaymentMethod"
            component={CheckboxField}
            label={formatMessage({ id: 'payment.paymentMethod.klarna.storePaymentMethod' })}
            data-test-id="store-klarna-payment-method"
          />
        )}
    </div>
  );
};

export const useKlarnaPayment = () => {
  const { checkoutState, updatePaymentOption } = useCheckoutState();
  const navigate = useNavigate();

  const authorizeKlarnaPayment = async (storeKlarnaPaymentMethod: boolean) => {
    const { paymentOptionKey, configuration } = checkoutState.orderState.payment!.selected![0];

    await updatePaymentOption({
      paymentOptionKey,
      specification: { initialization: { sendCustomerData: true } }
    });

    // We push the same route onto the history stack again so if users see a modal displayed from klarna and
    // use the browser back button to close it (which is not possible) they don't immediately drop out of the session
    navigate('/payment');
    const authorizationId = await new Promise<string>((resolve, reject) =>
      window.Klarna.Payments.authorize(
        { payment_method_category: configuration?.initializationData?.paymentCategory },
        storeKlarnaPaymentMethod ? { intent: 'buy_and_tokenize' } : {},
        async (res: { authorization_token?: string; approved: boolean; show_form: boolean; error?: any }) => {
          // Remove this log after SPAY klarna integration is done - Piotr Daszko
          if (!config.environment?.includes('live')) {
            console.log('Klarna authorization response: ', { res });
          }

          if (res.approved && res.authorization_token) {
            resolve(res.authorization_token);
          } else if (!res.show_form) {
            await updatePaymentOption({ paymentOptionKey, specification: { isDisabled: true } });
            reject(new Error('Klarna risk rejection'));
          } else {
            reject(res.error);
            pushErrorTrackingEventFromErrorObject(res.error, 'klarna');
          }
        }
      )
    );

    await updatePaymentOption({ paymentOptionKey, specification: { authorizationId } });
    return authorizationId;
  };

  // https://docs.klarna.com/conversion-boosters/express-checkout/integrate-express-checkout/integrate-multistep-express-checkout/
  const finalizeKlarnaPayment = async () => {
    const { paymentOptionKey } = checkoutState.orderState.payment!.selected![0];

    // Get klarna client_token from transaction
    // Ideally we should get this value from checkoutState.orderState.payment!.selected![0]
    const currentTransaction = await callApi({
      flow: 'express',
      method: 'GET',
      endpoint: `/transactions/${config.transactionId}`
    });
    const client_token = currentTransaction.providerData.client_token;

    // Mount Klarna script and initialize Klarna Payments
    await mountScript('https://x.klarnacdn.net/kp/lib/v1/api.js');
    window.Klarna.Payments.init({ client_token });

    // Finalize Klarna authorization and update payment option with authorizationId - This should not open a new widget
    const authorizationId = await new Promise<string>((resolve, reject) =>
      window.Klarna.Payments.finalize(
        {},
        null,
        async (res: { authorization_token?: string; approved: boolean; show_form: boolean; error?: any }) => {
          if (!config.environment?.includes('live')) {
            console.log('Klarna authorization response: ', { res });
          }

          if (res.approved && res.authorization_token) {
            resolve(res.authorization_token);
          } else if (!res.show_form) {
            reject(new Error('Klarna risk rejection'));
          } else {
            reject(res.error);
            pushErrorTrackingEventFromErrorObject(res.error, 'klarna');
          }
        }
      )
    );
    await updatePaymentOption({ paymentOptionKey, specification: { authorizationId } });
  };

  return { authorizeKlarnaPayment, finalizeKlarnaPayment };
};

export default KlarnaPayment;
